<template>
	<div class="container-fluid bg-default-color">
		<equipment-toolbar heading="Equipment" :action-items="actionItems" :mobile-breakpoint="mobileBreakpoint" v-on:actionItemClick="actionItemClick">
			
			<search-bar 
				:full-width='true' 
				placeholder="Search equipment..." 
				v-on:submit="searchEquipment"
				v-on:clearSearch="showSearchResults = false">
			</search-bar>

			<context-menu class="ml-2 mr-2"
				:menu-items="menuItems"
				v-on:editType="editType"
				v-on:editCat="editCat"
				v-on:newCategory="newCategory"
				v-on:newType="newType"
				v-on:deleteCategory="deleteCategory"
				v-on:deleteType="deleteType"
				v-on:newEquipment="newEquipment"
				v-on:exportEquipment="exportEquipment"
				v-on:importEquipment="importEquipment">
			</context-menu>

			<equipment-list-nav-tabs></equipment-list-nav-tabs>
			
		</equipment-toolbar>

		<field class="float-right"
			label="Include Archived"
			col-class="col-auto"
			:control-left="true"
			:params="{type: 'boolean'}"
			v-model="includeArchived">
		</field>
		<div style="clear: both;"></div>

		<div class="search mb-2">
			<div v-if="showSearchResults" class="mt-2">
				<p><a href="#" v-on:click.prevent="showSearchResults = false"><i class="fa fa-times"></i> Clear search</a></p>

				<p v-if="searchResults.length == 0">No matches found.</p>

				<div v-else class="list-group">
					<router-link v-for="equipment in mutatedSearchResults" 
						:to="'/equipment/edit/'+equipment.slug"
						:key="equipment.slug"
						class="list-group-item list-group-item-action">
						{{equipment.description}} <small>({{equipment.typeName}})</small>
					</router-link>
				</div>
			</div>
		</div>

		<equipment-picker v-if="!showSearchResults" 
			v-on:selectEvent="selectEvent"
			v-on:equipmentChange="editEquipment" 
			v-on:categoriesSet="pickerLists.categories = $event"
			v-on:typesSet="pickerLists.types = $event"
			v-on:equipmentSet="pickerLists.equipment = $event"
			v-model="editModal.selectedEquipmentId"
			:include-archived="includeArchived"
			:key="pickerKey">		
		</equipment-picker>

		<modal-dialog :modal-visible="importModal.visible"
			title="Import Equipment"
			confirm-button-text="Upload"
			cancel-button-text="Done"
			modal-size="modal-lg"
			v-on:confirm="uploadEquipmentCSV"
			v-on:closeModal="importModal.visible = false">

			<h3>Instructions</h3>

			<p>You can upload a .CSV file containing your equipment to import here.</p>

			<p>It should have row #1 as the field headings. Columns A-K should contain values mapping to the standard equipment fields, with any following columns containing any custom fields you have created for that type. </p>

			<p>Custom fields must be created in your Equipment Types before importing -- they will <strong>not</strong> be created automatically during the import process.</p>

			<p>Any custom field in the CSV that does not exist on the equipment type, will be ignored.</p>

			<p>Types must also exist prior to import. Any line that has an invalid type, will be ignored in the import process.</p>

			<h4>Fields:</h4>

			<ul>
				<li>type</li>
				<li>description</li>
				<li>serial_num</li>
				<li>date_acquired (YYYY-MM-DD format)</li>
				<li>next_service_date (YYYY-MM-DD format)</li>
				<li>next_service_description</li>
				<li>next_service_mileage_or_time</li>
				<li>last_service_date (YYYY-MM-DD format)</li>
				<li>last_service_mileage_or_time</li>
				<li>last_service_description</li>
				<li>Custom Field</li>
				<li>Custom Field 2</li>
				<li>etc. (more custom fields)</li>
			</ul>

			<p><strong>Example:</strong></p>

			<p>In the example below, we are importing equipment into 2 different <em>equipment types</em>. The <em>Dump Truck</em> type has a custom field called <em>Color</em>, while the <em>Skid Steer</em> type has a custom field called <em>Has Tracks</em>, to indicate whether the skid steer is a wheeled or tracked model.</p>

			<div style="overflow-x: auto;" class="mb-3">
				<table class="spreadsheet-table">
					<tr>
						<th></th>
						<th>A</th>
						<th>B</th>
						<th>C</th>
						<th>D</th>
						<th>E</th>
						<th>F</th>
						<th>G</th>
						<th>H</th>
						<th>J</th>
						<th>K</th>
						<th>L</th>
						<th>M</th>
					</tr>
					<tr>
						<th>1</th>
						<td>type</td>
						<td>description</td>
						<td>serial_num</td>
						<td>date_acquired</td>
						<td>next_service_date</td>
						<td>next_service_description</td>
						<td>next_service_mileage_or_time</td>
						<td>last_service_date</td>
						<td>last_service_mileage_or_time</td>
						<td>last_service_description</td>
						<td>Color</td>
						<td>Has Tracks</td>
					</tr>
					<tr>
						<th>2</th>
						<td>Dump Truck</td>
						<td>3500 GMC</td>
						<td>1GD31VCF229823</td>
						<td>2016-06-17</td>
						<td>2020-02-01</td>
						<td>Oil change</td>
						<td>46000</td>
						<td>2019-02-05</td>
						<td>41000</td>
						<td>Wheel bearing replacement</td>
						<td>Blue</td>
						<td></td>
					</tr>
					<tr>
						<th>3</th>
						<td>Dump Truck</td>
						<td>3500 GMC Quad Cab</td>
						<td>1GD411C87181704</td>
						<td>2016-04-11</td>
						<td>2020-04-13</td>
						<td>Tire rotation</td>
						<td>67000</td>
						<td>2019-02-05</td>
						<td>55000</td>
						<td>Emissions test</td>
						<td>Yellow</td>
						<td></td>
					</tr>
					<tr>
						<th>4</th>
						<td>Skid Steer</td>
						<td>Bobcat S590</td>
						<td>1GD31VCF2234823</td>
						<td>2016-06-17</td>
						<td>2020-08-06</td>
						<td>Oil change</td>
						<td>48600</td>
						<td>2019-02-09</td>
						<td>88500</td>
						<td>Oil Change</td>
						<td></td>
						<td>1</td>
					</tr>
				</table>
			</div>

			<input type="file" class="form-control mb-2" v-on:change="setFile">

		</modal-dialog>

		<modal-dialog :modal-visible="addCategoryModal.visible"
			title="Add an Equipment Category"
			confirm-button-text="Save"
			v-on:closeModal="addCategoryModal.visible = false"
			v-on:confirm="saveCategory">

			<field label="Category Name"
				col-class="col-md-4"
				v-model="addCategoryModal.name">
			</field>

			<field 
				label="Category Colour" 
				col-class="col-md-4" 
				v-model="addCategoryModal.color_hex" 
				:params="{type: 'color'}">
			</field>

		</modal-dialog>

		<modal-dialog :modal-visible="deleteModal.visible"
			:title="'Delete '+deleteModal.recordType"
			confirm-button-text="Yes, Delete"
			button-class="btn-outline-danger"
			v-on:closeModal="deleteModal.visible = false"
			v-on:confirm="confirmDelete">

			Are you sure you want to delete this {{deleteModal.recordType}}?

		</modal-dialog>

		<equipment-type-edit-modal 
			:visible="typeEditModal.visible"
			:title="typeEditModal.title"
			:record="typeEditModal.record"
			v-on:modelChanged="typeModelChanged"
			v-on:closeModal="typeEditModal.visible = false">
		</equipment-type-edit-modal>

	</div>
</template>
<script type="text/javascript">
import EquipmentToolbar from "./EquipmentToolbar"	
import TableList from "@/components/TableList"
import ModalDialog from "@/components/ModalDialog"
import EquipmentPicker from "@/components/equipment_picker/EquipmentPicker"
import SearchBar from "@/components/SearchBar"
import ContextMenu from "@/layout_fragments/ContextMenu"
import Field from "@/components/Field"
import EquipmentTypeEditModal from "@/components/super_components/EquipmentTypeEditModal"
import EquipmentListNavTabs from "./EquipmentListNavTabs"

export default {
	name: "EquipmentList",
	components: {
		EquipmentToolbar,
		TableList,
		ModalDialog,
		EquipmentPicker,
		SearchBar,
		ContextMenu,
		EquipmentTypeEditModal,
		Field,
		EquipmentListNavTabs
	},
	data() {
		return {
			typeSelected: null,
			catSelected: null,
			pickerLists: {
				equipment: [],
				types: [],
				categories: []
			},
			showSearchResults: false,
			includeArchived: false,
			searchResults: [],
			addCategoryModal: {
				visible: false,
				name: null,
				color_hex: null,
				id: null
			},
			typeEditModal: {
				visible: false,
				title: "New Equipment Type",
				record: {}
			},
			fields: [
				{label: "Description",key:"description"},
				{label: "Type",key:'typeName'},
				{label: "Serial #",key:"serial_num"},
				{label: "Next Service",key:"next_service_description"},
				{label: "Next Service Due",key:"nextServiceDue"}
				//{label: "Charge Out Rate",key:"charge_out_rate",align:"right"}
			],
			types: [],
			editModal: {
				visible: false,
				title: "Edit Equipment Item",
				record: {},
				selectedEquipmentId: null
			},
			deleteModal: {
				visible: false,
				recordType: "Category",
			},
			importModal: {
				visible: false,
				file: null
			},
			actionItems: [
				{text: 'New Equipment',icon:'fa-plus',link:'/equipment/edit/new'},
				{text: 'Import Equipment', icon:'fa-upload'}
			],
			mobileBreakpoint: 780,
			loading:true,
			pickerKey: Math.random()
		}
	},
	computed: {
		mobileUI() {
			if (window.innerWidth > this.mobileBreakpoint)
				return false
			else
				return true
		},
		menuItems() {
			let vm = this

			let items = []
			if (this.typeSelected) {
				items.push({text: 'Add Equipment',icon: 'fa-plus',clickFunction:'newEquipment'})

				items.push({
					text: 'Edit Type',
					icon:'fa-pencil-alt',
					clickFunction:'editType'
				})

				let deleteDisabled = true
				if (this.pickerLists.equipment.length == 0)
					deleteDisabled = false

				items.push({
					text: 'Delete Type',
					icon:'fa-trash',
					clickFunction:'deleteType',
					itemClass:'text-danger',
					disabled: deleteDisabled
				})

				items.push({text: 'Export this Type',icon:'fa-download',clickFunction:'exportEquipment'})
			}
			else if (this.catSelected) {
				items.push({text: 'Add Type',icon: 'fa-plus',clickFunction:'newType'})

				items.push({
					text: 'Edit Category',
					icon:'fa-pencil-alt',
					clickFunction:'editCat'
				})

				let deleteDisabled = true
				if (this.pickerLists.types.length == 0)
					deleteDisabled = false

				items.push({
					text: 'Delete Category',
					icon:'fa-trash',
					clickFunction:'deleteCategory',
					itemClass:'text-danger',
					disabled: deleteDisabled
				})

				items.push({text: 'Export this Category',icon:'fa-download',clickFunction:'exportEquipment'})
			}
			else {
				items.push({text: 'Add Category',icon: 'fa-plus',clickFunction:'newCategory'})
				items.push({text: 'Export Equipment',icon:'fa-download',clickFunction:'exportEquipment'})
			}
			
			items.push({text: 'Import Equipment',icon:'fa-upload',clickFunction:'importEquipment'})

			return items
		},
		mutatedEquipment() {
			return this.mutateEquipment(this.$store.equipments)
		},
		mutatedSearchResults() {
			return this.mutateEquipment(this.searchResults)
		}
	},
	methods: {
		typeModelChanged(record) {
			//this.pickerKey = Math.random()
			this.typeEditModal.record = record
			this.typeSelected = record
		},
		actionItemClick(item) {
			if (item.text == "Import Equipment") 
				this.importEquipment()
		},
		selectEvent(event) {

			if (event.event == "categorySelected")  {
				this.catSelected = event.obj

				window.history.pushState({},"","/equipment/"+this.catSelected.name.trim().slugify())
			}
			else if (event.event == "typeSelected") {
				this.typeSelected = event.obj

				window.history.pushState({},"","/equipment/"+this.catSelected.name.trim().slugify()+"/"+this.typeSelected.name.trim().slugify())
			}
			else if (event.event == "categoryDeselected") {
				this.catSelected = false
				window.history.pushState({},"","/equipment/list")
			}
			else if (event.event == "typeDeselected") {
				this.typeSelected = false
				window.history.pushState({},"","/equipment/"+this.catSelected.name.trim().slugify())
			}
			
			this.$store.lastRoute = window.location.pathname
		},
		newType() {
			this.typeEditModal.visible = true
			this.typeEditModal.title = "New Type"
			this.typeEditModal.record = {}
		},
		editType() {
			this.typeEditModal.visible = true
			this.typeEditModal.title = "Edit Type"
			this.typeEditModal.record = this.typeSelected

			if (!("EquipmentMaintenanceEventType" in this.typeSelected)) {
				this.getJSON("EquipmentType/find/"+this.typeSelected.slug,{include: ["EquipmentMaintenanceEventType"]}).then(response => {
					if (response.success) {
						this.typeEditModal.record = response.data
					}
				})
			}
		},
		deleteType() {
			this.deleteModal.visible = true
			this.deleteModal.recordType = "Type"
		},
		newCategory() {
			this.addCategoryModal.visible = true
			this.addCategoryModal.name = ""
		},
		editCat() {
			this.addCategoryModal.visible = true
			this.addCategoryModal.name = this.catSelected.name
			this.addCategoryModal.id = this.catSelected.id
		},
		saveCategory() {
			let vm = this

			let payload = {
				name : this.addCategoryModal.name,
				color_hex: this.addCategoryModal.color_hex
			}

			if (this.addCategoryModal.id)
				payload.id = this.addCategoryModal.id

			this.postData("EquipmentCategory/save",payload).then(response => {
				if (response.success) {
					vm.alertSaved("Category added")
					vm.addCategoryModal.visible = false
					//refresh pickerKey in order to reload equipmentpicker categories
					vm.pickerKey = Math.random()
				}
			})
		},
		deleteCategory() {
			this.deleteModal.visible = true
			this.deleteModal.recordType = "Category"
		},
		confirmDelete() {
			let vm = this

			let id
			if (this.deleteModal.recordType == 'Category')
				id = this.catSelected.id
			else if (this.deleteModal.recordType == 'Type')
				id = this.typeSelected.id

			this.postData("Equipment"+this.deleteModal.recordType+"/delete",{id:id},true).then(response => {
				if (response.success) {
					vm.alertSuccess(this.deleteModal.recordType+" deleted")

					vm.deleteModal.visible = false
					if (vm.deleteModal.recordType == 'Category')
						vm.catSelected = false
					else if (vm.deleteModal.recordType == "Type")
						vm.typeSelected = false

					vm.pickerKey = Math.random()
				}
			})
		},
		editEquipment(record) {
			this.editModal.selectedEquipmentId = record.id
			this.editModal.visible = true
			this.editModal.record = JSON.parse(JSON.stringify(record))
			this.editModal.title="Edit Equipment Item"

			this.$router.push("/equipment/edit/"+record.slug+"/log")
			this.$store.lastRoute = window.location.pathname
		},
		mutateEquipment(equipmentList) {
			let vm = this

			return equipmentList.map(eq => {
				eq.typeName = ""

				if (eq.EquipmentType)
					eq.typeName = eq.EquipmentType.name

				eq.nextServiceDue = ""

				if (eq.next_service_date) {
					let serviceDueInDays = vm.moment(eq.next_service_date).diff(vm.moment(),"days") + 1

					eq.nextServiceDue = eq.next_service_date+" ("+serviceDueInDays+" days)"

					if (eq.next_service_mileage_or_time) 
						eq.nextServiceDue += " / "+eq.next_service_mileage_or_time+ " "+eq.EquipmentType.odo_unit
					
				}

				return eq
			})
		},
		newEquipment() {
			let vm = this

			this.editModal.visible = true
			this.editModal.record = {}
			this.editModal.title = "Add New Equipment Item"

			this.$router.push("/equipment/edit/new")
		},
		closeEditModal() {
			this.editModal.visible = false
			this.editModal.selectedEquipmentId = null
		},
		importEquipment() {
			let vm = this

			if (this.types.length == 0) {
				this.getJSON("EquipmentType/get").then(response => {
					if (response.success) 
						vm.types = response.data
					
				})
			}

			this.importModal.visible = true
		},
		exportEquipment() {
			let vm = this

			let catId = 0

			let filters = []

			if (this.categorySelected) 
				filters = [["EquipmentType.category_id",this.categorySelected.id]]
			
			if (this.typeSelected) 
				filters.push(["type_id",this.typeSelected.id])

			filters = JSON.stringify(filters)

			let include = JSON.stringify([{model: "EquipmentType", include: ["EquipmentCategory"]}])

			window.location = window.apiBase+"Equipment/get?filters="+filters+"&include="+include+"&format=csv&token="+this.$store.sessionToken
		},
		setFile(evt) {
			if (evt.target.files.length > 0)
				this.importModal.file = evt.target.files[0]
		},
		uploadEquipmentCSV() {
			let vm = this

			let f = this.importModal.file

			let reader = new FileReader()

			let fileValid = true

			let allowedMimes = [
				"text/plain",
				"text/csv",
				"application/vnd.ms-excel"
			]

			//check file extension type
			if (allowedMimes.indexOf(f.type) == -1)
				vm.alertFail("Invalid file format! File must be a CSV")

			else {
				 // Closure to capture the file information.
				reader.onload = (theFile => {
					return e => {

						let csvObj = vm.CSVToArray(e.target.result)

						//check if last row is a blank, if so remove it
						if (csvObj[csvObj.length-1].length == 1)
							csvObj.splice(csvObj.length-1,1)

						let recordsPayload, resource

						//map rows to custom fields
						let staticFields = [
							"type",
							"description",
							"serial_num",
							"date_acquired",
							"next_service_date",
							"next_service_description",
							"next_service_mileage_or_time",
							"last_service_date",
							"last_service_mileage_or_time",
							"last_service_description"
						]

						let customFieldsMap = {}
						csvObj[0].forEach((headerCol,index) => {

							if (staticFields.indexOf(headerCol) < 0)
								customFieldsMap[headerCol] = index
						})

						let headerRow = JSON.parse(JSON.stringify(csvObj[0]))
						recordsPayload = csvObj.filter((row,index) => {
							if (index == 0) //ignore header row
								return false

							//check for valid equipment type
							let theType = vm.types.find(type => type.name == row[0])

							if (theType)
								return true
							else 
								return false
						}).map(row => {
							//get type id
							let type = vm.types.find(type => type.name == row[0])

							let typeCustomFields = JSON.parse(type.custom_fields_json)

							let customFields = {}
							typeCustomFields.forEach(typeCustomField => {
								if (typeCustomField.label in customFieldsMap) {
									customFields[typeCustomField.label] = row[customFieldsMap[typeCustomField.label]]
								}
							})

							let returnObj = {
								type_id: type.id,
								custom_fields_json: JSON.stringify(customFields)
							}

							staticFields.forEach((field,index) => {
								if (index > 0) //skip type
									returnObj[field] = row[index]
							})

							return returnObj
						})

						vm.spinner(true)

						vm.postData("Equipment/save-batch",{records: JSON.stringify(recordsPayload)}).then(response => {
							vm.alertSaved(response.data.records_saved+" items of equipment imported successfully!")
							vm.spinner(false)

							vm.fetchData()

							vm.importModal.visible = false
						})
					};
				})(f);

				reader.readAsText(f);
			}
		},
		searchEquipment(terms) {
			let vm = this

			let params = {
				filters: [
					["description",terms,"and","like"],
					["serial_num",terms,"or","like"]
				],
				include: ["EquipmentType"]
			}

			this.getJSON("Equipment/get",params,true).then(response => {
				if (response.success) {
					vm.showSearchResults = true
					vm.searchResults = response.data
				}
			})
		},
		/*fetchTypes() {
			let vm = this

			return new Promise((resolve,reject) => {

				let params = {}

				vm.getJSON("EquipmentType/get",params,true).then(response => {

					resolve(response)
				})

			})
		},*/
		fetchData() {
			let vm = this

			let promises = [
				this.fetchEquipment()
			]

			this.spinner(true)
			this.loading = true

			return new Promise((resolve,reject) => {
				Promise.all(promises).then(responses => {
					vm.spinner(false)
					vm.loading = false

					resolve(responses)
				})
			})
		}
	},
	created() {
		let vm = this
		this.fetchData().then(responses => {
			if ("slug" in vm.$route.params && vm.$route.params.slug) {
				//load modal
				let equipment = vm.mutatedEquipment.find(eq => eq.slug == vm.$route.params.slug)

				if (equipment)
					vm.editEquipment(equipment)
			}
			else {

				let dualSlug = ""
				if ("cat_slug" in vm.$route.params) 
					dualSlug = vm.$route.params.cat_slug
				if ("type_slug" in vm.$route.params)
					dualSlug += "/"+vm.$route.params.type_slug

				vm.editModal.selectedEquipmentId = dualSlug //set the slugs as the id so the EquipmentPicker component will pick it up and handle it accordingly
			}
		})
	}
}
</script>